/**
 * SADemoPlayer — Web Audio API engine for demo playback.
 *
 * Plays short audio loops (FLAC/WAV/MP3) with sample-accurate
 * source switching. Decoupled from UI — call API methods and
 * listen for events.
 *
 * Usage:
 *   var player = new SADemoPlayer(config.groups);
 *   player.on('statechange', function(state) { ... });
 *   player.on('timeupdate', function(state) { ... });
 *   player.switchGroup(0); // loads + plays first group
 */
(function() {
  'use strict';

  var demoOwnerSeq = 0;

  function SADemoPlayer(groups) {
    this._groups = groups;
    this._ctx = null;
    this._buffers = {};
    this._loadState = {};
    this._activeGroup = -1;
    this._activeSource = 0;
    this._playing = false;
    this._sourceNode = null;
    this._startedAt = 0;
    this._offsetAt = 0;
    this._duration = 0;
    this._listeners = {};
    this._timeupdateId = null;
    this._analyser = null;
    this._mediaOwnerId = 'sa-demo-player-' + (++demoOwnerSeq);
    this._playVersion = 0;

    for (var g = 0; g < groups.length; g++) {
      for (var s = 0; s < groups[g].sources.length; s++) {
        this._loadState[g + '-' + s] = 'idle';
      }
    }

    if (window.SAMediaCoordinator) {
      var self = this;
      window.SAMediaCoordinator.register(this._mediaOwnerId, {
        pause: function() { self.pause(); }
      });
    }
  }

  SADemoPlayer.prototype.on = function(event, fn) {
    if (!this._listeners[event]) this._listeners[event] = [];
    this._listeners[event].push(fn);
  };

  SADemoPlayer.prototype.off = function(event, fn) {
    if (!this._listeners[event]) return;
    this._listeners[event] = this._listeners[event].filter(function(f) { return f !== fn; });
  };

  SADemoPlayer.prototype._emit = function(event, data) {
    var fns = this._listeners[event];
    if (!fns) return;
    for (var i = 0; i < fns.length; i++) fns[i](data);
  };

  /* ── iOS silent switch bypass ──
     iOS routes Web Audio through the ringer channel, which the
     physical silent switch mutes. The Audio Session API (Safari 17.4+)
     lets us declare "playback" intent, which tells iOS to use the
     media channel instead.

     Previous approach: a hidden <audio> element looping a silent MP3
     (data URI, base64-encoded) forced iOS to switch to the media
     channel. This worked but had serious drawbacks:
       - The silent audio looped continuously, draining battery
       - Required global event listeners on every click/touch/keydown
         to keep the tag alive via user-gesture context
       - Complex lifecycle: priming timers, pause countdowns, section-
         scoped listeners, and shared global state between engines
       - First-play timing issues — the channel switch had to happen
         before Web Audio started, requiring priming on unrelated taps

     The Audio Session API replaces all of that with a single property
     assignment. No hidden elements, no looping audio, no battery drain.
     Called synchronously from play() in the user-gesture context. */

  function _setPlaybackSession() {
    if (navigator.audioSession) {
      navigator.audioSession.type = 'playback';
    }
  }

  SADemoPlayer.prototype._ensureContext = function() {
    if (!this._ctx) {
      _setPlaybackSession();
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
      window._saSharedAudioCtx = this._ctx;
      this._analyser = this._ctx.createAnalyser();
      this._analyser.fftSize = 2048;
      this._analyser.smoothingTimeConstant = 0.6;
      this._analyser.minDecibels = -100;
      this._analyser.maxDecibels = 0;
    }
    if (this._ctx.state === 'suspended') {
      return this._ctx.resume();
    }
    return Promise.resolve();
  };

  SADemoPlayer.prototype.getAnalyser = function() {
    return this._analyser;
  };

  SADemoPlayer.prototype._bufferKey = function(groupIdx, sourceIdx) {
    return groupIdx + '-' + sourceIdx;
  };

  SADemoPlayer.prototype._loadSource = function(groupIdx, sourceIdx) {
    var self = this;
    var key = this._bufferKey(groupIdx, sourceIdx);

    if (this._loadState[key] === 'loading' || this._loadState[key] === 'ready') {
      return Promise.resolve(this._buffers[key]);
    }

    this._loadState[key] = 'loading';
    this._emit('statechange', this.getState());

    var url = this._groups[groupIdx].sources[sourceIdx].url;

    return fetch(url)
      .then(function(res) {
        if (!res.ok) throw new Error('Failed to fetch ' + url + ': ' + res.status);
        return res.arrayBuffer();
      })
      .then(function(arrayBuf) {
        return self._ctx.decodeAudioData(arrayBuf);
      })
      .then(function(audioBuf) {
        self._buffers[key] = audioBuf;
        self._loadState[key] = 'ready';
        self._emit('statechange', self.getState());
        return audioBuf;
      })
      .catch(function(err) {
        self._loadState[key] = 'idle';
        self._emit('statechange', self.getState());
        console.error('SADemoPlayer: load error', err);
        throw err;
      });
  };

  SADemoPlayer.prototype._prefetchGroup = function(groupIdx) {
    var group = this._groups[groupIdx];
    for (var s = 0; s < group.sources.length; s++) {
      this._loadSource(groupIdx, s);
    }
  };

  SADemoPlayer.prototype._getPosition = function() {
    if (!this._playing || !this._ctx) return this._offsetAt;
    var elapsed = this._ctx.currentTime - this._startedAt + this._offsetAt;
    if (this._duration > 0) elapsed = elapsed % this._duration;
    return elapsed;
  };

  SADemoPlayer.prototype._startTimeupdate = function() {
    var self = this;
    if (this._timeupdateId) return;
    function tick() {
      self._emit('timeupdate', self.getState());
      self._timeupdateId = requestAnimationFrame(tick);
    }
    this._timeupdateId = requestAnimationFrame(tick);
  };

  SADemoPlayer.prototype._stopTimeupdate = function() {
    if (this._timeupdateId) {
      cancelAnimationFrame(this._timeupdateId);
      this._timeupdateId = null;
    }
  };

  SADemoPlayer.prototype._stopSource = function() {
    if (this._sourceNode) {
      try { this._sourceNode.stop(); } catch (e) {}
      this._sourceNode.disconnect();
      this._sourceNode = null;
    }
  };

  SADemoPlayer.prototype._playBuffer = function(buffer, offset) {
    if (window.SAMediaCoordinator) {
      window.SAMediaCoordinator.claim(this._mediaOwnerId);
    }
    this._stopSource();
    var node = this._ctx.createBufferSource();
    node.buffer = buffer;
    node.loop = true;
    node.connect(this._analyser);
    node.connect(this._ctx.destination);

    this._duration = buffer.duration;
    this._offsetAt = offset % this._duration;
    this._startedAt = this._ctx.currentTime;

    node.start(0, this._offsetAt);
    this._sourceNode = node;
    this._playing = true;
    this._startTimeupdate();
    this._emit('statechange', this.getState());
  };

  SADemoPlayer.prototype.play = function() {
    if (this._activeGroup < 0) return;
    var self = this;
    var playVersion = ++this._playVersion;
    _setPlaybackSession();

    this._ensureContext().then(function() {
      if (playVersion !== self._playVersion) return;
      var key = self._bufferKey(self._activeGroup, self._activeSource);
      var buffer = self._buffers[key];

      if (buffer) {
        self._playBuffer(buffer, self._offsetAt);
      } else {
        self._loadSource(self._activeGroup, self._activeSource).then(function(buf) {
          if (playVersion === self._playVersion && self._activeGroup >= 0) {
            self._playBuffer(buf, 0);
            self._prefetchGroup(self._activeGroup);
          }
        });
      }
    });
  };

  SADemoPlayer.prototype.pause = function() {
    this._playVersion++;
    if (!this._playing) {
      if (window.SAMediaCoordinator) {
        window.SAMediaCoordinator.release(this._mediaOwnerId);
      }
      return;
    }
    this._offsetAt = this._getPosition();
    this._stopSource();
    this._playing = false;
    this._stopTimeupdate();
    if (window.SAMediaCoordinator) {
      window.SAMediaCoordinator.release(this._mediaOwnerId);
    }
    this._emit('statechange', this.getState());
  };

  SADemoPlayer.prototype.switchSource = function(sourceIdx) {
    if (this._activeGroup < 0) return;
    if (sourceIdx === this._activeSource) return;

    var wasPlaying = this._playing;
    var pos = this._getPosition();

    this._activeSource = sourceIdx;

    if (wasPlaying) {
      var self = this;
      var playVersion = ++this._playVersion;
      this._ensureContext().then(function() {
        if (playVersion !== self._playVersion) return;
        var key = self._bufferKey(self._activeGroup, sourceIdx);
        var buffer = self._buffers[key];
        if (buffer) {
          self._playBuffer(buffer, pos);
        } else {
          self._stopSource();
          self._playing = false;
          self._stopTimeupdate();
          self._emit('statechange', self.getState());
          self._loadSource(self._activeGroup, sourceIdx).then(function(buf) {
            if (playVersion !== self._playVersion) return;
            self._playBuffer(buf, pos);
          });
        }
      });
    } else {
      this._emit('statechange', this.getState());
    }
  };

  SADemoPlayer.prototype.switchGroup = function(groupIdx) {
    if (groupIdx === this._activeGroup) {
      if (this._playing) this.pause();
      else this.play();
      return;
    }

    this._stopSource();
    this._playing = false;
    this._stopTimeupdate();
    this._offsetAt = 0;
    this._activeGroup = groupIdx;
    this._activeSource = 0;

    this._emit('statechange', this.getState());

    var self = this;
    var playVersion = ++this._playVersion;
    this._ensureContext().then(function() {
      self._loadSource(groupIdx, 0).then(function(buf) {
        if (playVersion === self._playVersion && self._activeGroup === groupIdx) {
          self._playBuffer(buf, 0);
          self._prefetchGroup(groupIdx);
        }
      });
    });
  };

  SADemoPlayer.prototype.getState = function() {
    var loadingStates = {};
    if (this._activeGroup >= 0) {
      var group = this._groups[this._activeGroup];
      for (var s = 0; s < group.sources.length; s++) {
        loadingStates[s] = this._loadState[this._bufferKey(this._activeGroup, s)];
      }
    }
    return {
      activeGroup: this._activeGroup,
      activeSource: this._activeSource,
      playing: this._playing,
      position: this._getPosition(),
      duration: this._duration,
      loading: loadingStates
    };
  };

  SADemoPlayer.prototype.destroy = function() {
    this._stopSource();
    this._stopTimeupdate();
    if (window.SAMediaCoordinator) {
      window.SAMediaCoordinator.release(this._mediaOwnerId);
    }
    if (this._ctx) {
      this._ctx.close();
      this._ctx = null;
    }
    this._buffers = {};
    this._listeners = {};
  };

  window.SADemoPlayer = SADemoPlayer;
})();
